/**
 * La lista delle conversazioni: intestazione con ricerca, banner dei filtri,
 * card per thread e indice di urgenza.
 *
 * Estratta da `MessaggiPage.tsx` il 2026-09-01. Il motivo e' il cricchetto del
 * tetto righe: quel file era gia' a 2017 righe, ben oltre le 800, e la regola e'
 * che un file cosi' possa solo rimpicciolire. Per aggiungergli l'avviso della
 * ricerca e il conteggio onesto bisognava prima trovare una casa migliore a
 * qualcosa — ed e' esattamente il lavoro che quel gate serve a innescare.
 *
 * Il blocco era gia' autocontenuto: le sue uniche dipendenze sono import da
 * moduli condivisi, quindi lo spostamento e' meccanico.
 */
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import { MarketplaceLogo } from "@/shared/ui/MarketplaceLogo";
import { formatRelative } from "@/shared/format";
import { hasActiveTextSelection } from "@/shared/textSelection";
import { raggruppaPerGiorno } from "@/features/ordini/giorniLista";
import {
  FORMA_BADGE,
  contorno,
  pieno,
  type StatoTone,
} from "@/features/ordini/components/statoToni";
import type { GroupedThread } from "../threadGrouping";

// Redesign 2026-10-27 (dopo la lista ordini): le conversazioni sono card bianche
// su carta, divise per giorno come gli ordini, e i badge hanno la forma e i
// colori delle etichette di stato della pagina ordini. L'urgenza non e' piu' un
// filo colorato a sinistra (si vedeva solo da vicino): e' un'etichetta piena.

export function ThreadList({
  items,
  total,
  mostrati,
  inRicerca,
  isLoading,
  error,
  info,
  search,
  onSearchChange,
  activeId,
  onSelect,
  filtersActive,
  onResetFilters,
}: {
  items: GroupedThread[];
  total: number;
  /** Quanti thread il backend ha davvero restituito (la GET chiede limit=100). */
  mostrati: number;
  inRicerca: boolean;
  isLoading: boolean;
  error: string | null;
  info: string | null;
  search: string;
  onSearchChange: (v: string) => void;
  activeId: number | null;
  onSelect: (id: number) => void;
  filtersActive: boolean;
  onResetFilters: () => void;
}) {
  // La lista arriva gia' ordinata per ultimo messaggio: si spezza per giorno
  // senza riordinare (stessa regola della lista ordini, giorno di Roma).
  const giorni = raggruppaPerGiorno(items, (t) => t.last_message_at);
  return (
    <section className="flex min-h-0 flex-col border-r border-line bg-paper">
      <div className="flex-shrink-0 border-b border-line bg-card px-3.5 pb-3 pt-3.5">
        <div className="mb-2.5 flex items-baseline justify-between gap-2">
          <h2 className="font-display text-[19px] font-bold tracking-[-0.01em] text-ink">
            Conversazioni
          </h2>
          {/* La GET chiede limit=100 e non c'è paginazione: il totale da solo
              direbbe 660 mostrandone 100, senza che nulla lo segnali. */}
          <span className="text-[12px] font-semibold tabular-nums text-ink">
            {mostrati < total ? `${mostrati} di ${total}` : `${total}`}{" "}
            {total === 1 ? "conversazione" : "conversazioni"}
          </span>
        </div>
        <div className="flex h-[38px] items-center gap-2 rounded-full border border-line-2 bg-card px-3.5 transition-[border-color,box-shadow] focus-within:border-ink focus-within:shadow-[0_0_0_3px_rgba(31,27,22,0.08)]">
          <svg
            className="h-4 w-4 shrink-0 fill-none stroke-current stroke-[1.8] text-ink [stroke-linecap:round] [stroke-linejoin:round]"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7.5" />
            <path d="M20.5 20.5l-4.2-4.2" />
          </svg>
          <input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Cerca cliente, oggetto, ordine…"
            aria-label="Cerca conversazioni"
            className="min-w-0 flex-1 border-none bg-transparent text-[13.5px] text-ink outline-none placeholder:text-ink-3"
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange("")}
              aria-label="Cancella ricerca"
              className="-mr-1.5 grid h-7 w-7 shrink-0 place-items-center rounded-full text-ink hover:bg-paper-2"
            >
              <svg
                className="h-3.5 w-3.5 fill-none stroke-current stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round]"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {inRicerca && (
        <p className="border-b border-line bg-card px-3.5 py-2 text-[12px] text-ink">
          Ricerca attiva: cerco in <b className="font-bold">tutti gli stati</b>, non solo nella
          vista selezionata.
        </p>
      )}

      {filtersActive && (
        <button
          type="button"
          onClick={onResetFilters}
          className="flex w-full items-center justify-between gap-2 border-b border-amber/25 bg-amber-soft px-3.5 py-2 text-left text-[12px] text-ink transition-[filter] hover:brightness-95"
        >
          <span className="inline-flex items-center gap-1.5">
            <svg
              className="h-3.5 w-3.5 shrink-0 fill-none stroke-current stroke-[1.8] [stroke-linecap:round] [stroke-linejoin:round]"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" />
            </svg>
            Filtri attivi — alcuni messaggi potrebbero essere nascosti
          </span>
          <span className="shrink-0 font-bold underline underline-offset-2">Mostra tutti</span>
        </button>
      )}

      <div className="flex-1 overflow-y-auto pb-3 scroll-paper">
        <ErrorBanner message={error} className="m-3" />
        {/* Audit 2026-05-29: esito positivo del sync in verde (emerald),
            separato dal box errore rosso. */}
        {info && !error && (
          <div className="m-3 rounded-xl border border-green/30 bg-green-soft px-3 py-2 text-[12.5px] text-ink">
            {info}
          </div>
        )}
        {isLoading && items.length === 0 ? (
          <p className="px-3 py-8 text-center text-[13px] text-ink">Carico le conversazioni…</p>
        ) : items.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <p className="text-[14px] font-bold text-ink">Nessuna conversazione</p>
            <p className="mt-1 text-[12.5px] text-ink">
              Avvia una sincronizzazione per scaricare i messaggi dai marketplace collegati.
            </p>
          </div>
        ) : (
          giorni.map((giorno) => (
            <div key={giorno.chiave} role="group" aria-label={giorno.titolo}>
              <div className="sticky top-0 z-[1] flex items-baseline gap-1.5 bg-paper/95 px-3.5 pb-1.5 pt-3 backdrop-blur-[2px]">
                <span className="text-[13px] font-bold text-ink">{giorno.titolo}</span>
                {giorno.sottotitolo && (
                  <span className="text-[12px] text-ink">{giorno.sottotitolo}</span>
                )}
                <span className="ml-auto text-[11.5px] tabular-nums text-ink">
                  {giorno.ordini.length}
                </span>
              </div>
              <div className="space-y-1.5 px-2">
                {giorno.ordini.map((thread) => (
                  <ThreadCard
                    key={thread.id}
                    thread={thread}
                    active={activeId === thread.id}
                    onClick={() => {
                      // Selezione testo in corso (copia mittente/oggetto): no switch.
                      if (hasActiveTextSelection()) return;
                      onSelect(thread.id);
                    }}
                  />
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

type UrgencyToken = "ok" | "warning" | "critical";

/**
 * Indice urgenza per un thread che richiede risposta.
 *
 * Replica adattata del legacy ``tab_messaggi.py::_urgency_border``
 * (riga 141). L'utente ha richiesto soglie 23h/24h (audit 2026-05-14):
 * - < 23h dall'ultimo messaggio: «Da rispondere»
 * - 23-24h: «Urgente» (1h prima del SLA marketplace tipico 24h)
 * - >= 24h: «SLA scaduto» (rischio penali Mirakl/Amazon)
 *
 * Ritorna ``null`` quando il thread non richiede risposta o manca il
 * timestamp: in quel caso il ThreadCard non mostra alcun indicatore.
 */
function urgencyToken(lastMessageAt: string | null, needsReply: boolean): UrgencyToken | null {
  if (!needsReply || !lastMessageAt) return null;
  const ts = Date.parse(lastMessageAt);
  if (Number.isNaN(ts)) return null;
  const hours = (Date.now() - ts) / 3_600_000;
  if (hours >= 24) return "critical";
  if (hours >= 23) return "warning";
  return "ok";
}

// I toni delle etichette di stato ordini: arancio = da fare (come «Da
// evadere»), oro = sta per scadere, rosso = scaduto (come «Da pagare»).
const URGENCY_TONO: Record<UrgencyToken, StatoTone> = {
  ok: "in-corso",
  warning: "bonifico",
  critical: "da-pagare",
};

const URGENCY_LABEL: Record<UrgencyToken, string> = {
  ok: "Da rispondere",
  warning: "Urgente · <1h",
  critical: "SLA scaduto",
};

const URGENCY_TITLE: Record<UrgencyToken, string> = {
  ok: "Thread in attesa di risposta entro 24h",
  warning: "Ultimo messaggio tra 23 e 24h — meno di 1h al limite SLA",
  critical: "Ultimo messaggio oltre 24h — SLA marketplace tipicamente scaduto",
};

function ThreadCard({
  thread,
  active,
  onClick,
}: {
  thread: GroupedThread;
  active: boolean;
  onClick: () => void;
}) {
  // Più conversazioni Mirakl sullo stesso ordine, fuse in questa card.
  const conversazioni = thread.groupThreadIds.length;
  const urgency = urgencyToken(thread.last_message_at, thread.needs_reply);
  const canale = thread.source_label || thread.source;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active || undefined}
      className={[
        "block w-full rounded-xl border px-3 py-2.5 text-left transition-[border-color,box-shadow,background-color] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30",
        active
          ? "border-ink bg-card shadow-[0_0_0_1px_#1F1B16,0_4px_14px_rgba(31,27,22,0.08)]"
          : "border-line bg-card hover:border-line-2 hover:shadow-cardOpen",
      ].join(" ")}
    >
      <div className="flex items-center gap-2">
        <MarketplaceLogo source={thread.source} size={18} label={canale || undefined} />
        <span
          className={[
            "min-w-0 flex-1 truncate text-[14px] text-ink",
            thread.has_unread ? "font-bold" : "font-semibold",
          ].join(" ")}
        >
          {thread.customer_name || thread.customer_org_name || "Cliente"}
        </span>
        {thread.has_unread && (
          <span
            className="h-2 w-2 shrink-0 rounded-full bg-accent"
            title="Non letto"
            aria-label="Non letto"
          />
        )}
        <span className="flex-shrink-0 text-[11.5px] tabular-nums text-ink">
          {formatRelative(thread.last_message_at)}
        </span>
      </div>
      <div className="mt-1 truncate text-[13px] font-semibold text-ink">
        {thread.subject || "(senza oggetto)"}
      </div>
      <div className="mt-0.5 line-clamp-2 text-[12.5px] leading-snug text-ink">
        {thread.last_message_preview || "—"}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {urgency && (
          <span
            className={FORMA_BADGE}
            style={pieno(URGENCY_TONO[urgency])}
            title={URGENCY_TITLE[urgency]}
          >
            {URGENCY_LABEL[urgency]}
          </span>
        )}
        {conversazioni > 1 && (
          <span
            className={FORMA_BADGE}
            style={contorno("sost-fornitore")}
            data-testid="thread-group-badge"
            title={`Il cliente ha aperto ${conversazioni} conversazioni su questo ordine: qui sono unite in una sola, in ordine di data.`}
          >
            {conversazioni} conversazioni
          </span>
        )}
        <span className="min-w-0 truncate text-[11.5px] text-ink">
          {canale}
          {thread.external_order_id && (
            <>
              {" · "}
              <b className="font-semibold tabular-nums">{thread.external_order_id}</b>
            </>
          )}
        </span>
      </div>
    </button>
  );
}
